/*
  Produktfinder Curantus – Engine
  Custom Elements <produkt-finder> und <finder-result-bar>.
  Vanilla ES2020, keine Abhängigkeiten. Reine Logik wird unter window.Produktfinder exportiert
  (testbar in Node: globalThis.window = {} und Datei per vm laden).
*/
(function () {
  'use strict';

  const NS = 'Produktfinder';
  const CONFIG_ID = 'ProduktfinderConfig';
  const STORAGE_KEY = 'produktfinder:state';
  const FACET_SECTION = 'finder-facets';
  const PARAM_META = 'filter.p.m.filter.';
  const PARAM_GENDER = 'filter.p.m.custom.geschlecht';
  const PARAM_PRICE = 'filter.v.price';
  const PARAM_PRICE_GTE = 'filter.v.price.gte';
  const PARAM_PRICE_LTE = 'filter.v.price.lte';
  const MAX_PARALLEL = 6;
  const FETCH_TIMEOUT = 8000;
  const CONFIRM_DELAY = 150;

  const win = typeof window !== 'undefined' ? window : globalThis;
  const hasDOM = typeof document !== 'undefined' && typeof HTMLElement !== 'undefined' && typeof customElements !== 'undefined';

  function warn() {
    try {
      const args = Array.prototype.slice.call(arguments);
      args.unshift('[' + NS + ']');
      if (typeof console !== 'undefined' && console.warn) console.warn.apply(console, args);
    } catch (e) { /* nichts */ }
  }

  /* ------------------------------------------------------------------ */
  /* Reine Logik                                                         */
  /* ------------------------------------------------------------------ */

  function paramName(key) {
    if (key === 'custom.geschlecht') return PARAM_GENDER;
    if (key === 'price') return PARAM_PRICE;
    return PARAM_META + key;
  }

  function uniq(list) {
    const out = [];
    list.forEach((v) => { if (out.indexOf(v) < 0) out.push(v); });
    return out;
  }

  /* Regel 4: gewählte Werte um Expansions ergänzen ("*" gilt für jeden Wert). */
  function applyExpansions(key, values, expansions) {
    const base = Array.isArray(values) ? values.slice() : [];
    const rules = expansions && key ? expansions[key] : null;
    if (!rules) return uniq(base);
    const out = base.slice();
    base.forEach((v) => {
      if (Array.isArray(rules['*'])) out.push.apply(out, rules['*']);
      if (Array.isArray(rules[v])) out.push.apply(out, rules[v]);
    });
    return uniq(out);
  }

  /* Alle Wertegruppen einer Option: step.filter-Werte (+Expansions) und extra-Werte (+Expansions). */
  function optionValueGroups(step, option, expansions) {
    const groups = [];
    const key = step && step.filter;
    if (key && key !== 'price' && Array.isArray(option.values) && option.values.length) {
      groups.push({ key, param: paramName(key), values: applyExpansions(key, option.values, expansions), mode: step.mode === 'soft' ? 'soft' : 'hard' });
    }
    if (Array.isArray(option.extra)) {
      option.extra.forEach((e) => {
        if (!e || !e.key || !Array.isArray(e.values) || !e.values.length) return;
        groups.push({ key: e.key, param: paramName(e.key), values: applyExpansions(e.key, e.values, expansions), mode: e.mode === 'soft' ? 'soft' : (e.mode === 'hard' ? 'hard' : (step.mode === 'soft' ? 'soft' : 'hard')) });
      });
    }
    return groups;
  }

  function findOption(step, id) {
    if (!step || !Array.isArray(step.options)) return null;
    for (let i = 0; i < step.options.length; i++) if (step.options[i] && step.options[i].id === id) return step.options[i];
    return null;
  }

  /*
    Antworten -> Zielkollektion, Filterliste (Reihenfolge = Einfügereihenfolge; gleicher Key wird ODER-verknüpft,
    hart schlägt weich) und Preis.
    answers: [{ stepId, optionIds[], skipped }]
  */
  function deriveFilters(config, answers, initialCollection) {
    const state = { collection: initialCollection || '', filters: [], price: null };
    const byKey = Object.create(null);
    const steps = (config && config.steps) || {};
    const expansions = (config && config.expansions) || {};

    function add(group) {
      let f = byKey[group.key];
      if (!f) {
        f = { key: group.key, param: group.param, values: [], mode: group.mode };
        byKey[group.key] = f;
        state.filters.push(f);
      }
      f.values = uniq(f.values.concat(group.values));
      if (group.mode === 'hard') f.mode = 'hard';
    }

    (answers || []).forEach((a) => {
      const step = steps[a.stepId];
      if (!step || a.skipped) return;
      (a.optionIds || []).forEach((id) => {
        const opt = findOption(step, id);
        if (!opt) return;
        if (opt.collection) state.collection = opt.collection;
        if (step.filter === 'price' && opt.price) {
          const p = state.price || {};
          if (opt.price.gte != null) p.gte = p.gte != null ? Math.max(p.gte, opt.price.gte) : opt.price.gte;
          if (opt.price.lte != null) p.lte = p.lte != null ? Math.min(p.lte, opt.price.lte) : opt.price.lte;
          state.price = p;
        }
        optionValueGroups(step, opt, expansions).forEach(add);
      });
    });
    return state;
  }

  function normalizeRoot(root) {
    let r = typeof root === 'string' && root ? root : '/';
    if (r.charAt(r.length - 1) !== '/') r += '/';
    return r;
  }

  function buildParams(filters, price) {
    const params = new URLSearchParams();
    const hard = [];
    const soft = [];
    (filters || []).forEach((f) => (f.mode === 'soft' ? soft : hard).push(f));
    hard.concat(soft).forEach((f) => {
      (f.values || []).forEach((v) => params.append(f.param || paramName(f.key), v));
    });
    if (price) {
      if (price.gte != null) params.append(PARAM_PRICE_GTE, String(price.gte));
      if (price.lte != null) params.append(PARAM_PRICE_LTE, String(price.lte));
    }
    return params;
  }

  /* Ergebnis-URL: harte Filter zuerst, dann weiche, Preis, finder=1. */
  function buildUrl(opts) {
    const o = opts || {};
    const params = buildParams(o.filters, o.price);
    params.append('finder', '1');
    return normalizeRoot(o.root) + 'collections/' + encodeURIComponent(o.collection || '') + '?' + params.toString();
  }

  /* Facetten-URL (Section Rendering API). */
  function facetUrl(opts) {
    const o = opts || {};
    const params = buildParams(o.filters, o.price);
    params.append('section_id', FACET_SECTION);
    return normalizeRoot(o.root) + 'collections/' + encodeURIComponent(o.collection || '') + '?' + params.toString();
  }

  /* Antwort der Facetten-Section (HTML) -> Objekt oder null. */
  function parseFacets(text) {
    if (typeof text !== 'string') return null;
    const m = text.match(/<script[^>]*data-finder-facets[^>]*>([\s\S]*?)<\/script>/i);
    if (!m) return null;
    try {
      const data = JSON.parse(m[1].trim());
      if (!data || data.error || typeof data !== 'object') return null;
      if (!data.filters || typeof data.filters !== 'object') data.filters = {};
      return data;
    } catch (e) {
      return null;
    }
  }

  /*
    Regel 1/2: Sichtbarkeit und Zähler je Option aus einer Facetten-Antwort.
    Rückgabe: { options: [{ id, visible, count, label, exact, routing }], visibleCount, degraded }
  */
  function evaluateOptions(step, facets, expansions, minResults) {
    const min = Number(minResults) > 0 ? Number(minResults) : 1;
    const result = { options: [], visibleCount: 0, degraded: false };
    if (!step || !Array.isArray(step.options)) return result;
    const noFacets = !facets || typeof facets !== 'object' || !facets.filters;
    const routingStep = !step.filter;
    const priceStep = step.filter === 'price';
    const soft = step.mode === 'soft';

    step.options.forEach((opt) => {
      if (!opt) return;
      const groups = optionValueGroups(step, opt, expansions);
      const entry = { id: opt.id, visible: true, count: null, label: null, exact: false, routing: groups.length === 0 };
      if (routingStep || priceStep || entry.routing) {
        result.options.push(entry);
        result.visibleCount++;
        return;
      }
      if (noFacets) {
        result.degraded = true;
        result.options.push(entry);
        result.visibleCount++;
        return;
      }
      // Eigene Werte der Option (ohne Expansions wie „Alle Hauttypen“) – nur sie bestimmen den angezeigten Zähler,
      // sonst zeigt jede Hauttyp-Option die Zahl der Universalprodukte. Sichtbarkeit rechnet weiterhin mit allen Werten.
      const own = new Set(Array.isArray(opt.values) ? opt.values : []);
      (Array.isArray(opt.extra) ? opt.extra : []).forEach((e) => (Array.isArray(e && e.values) ? e.values : []).forEach((v) => own.add(v)));
      let sum = 0;
      let max = 0;
      let maxOwn = 0;
      let n = 0;
      groups.forEach((g) => {
        const facet = facets.filters[g.param];
        const values = facet && facet.values && typeof facet.values === 'object' ? facet.values : {};
        g.values.forEach((v) => {
          const c = Number(values[v]) || 0;
          sum += c;
          if (c > max) max = c;
          if (own.has(v) && c > maxOwn) maxOwn = c;
          n++;
        });
      });
      entry.exact = n === 1;
      entry.count = entry.exact ? sum : (maxOwn || max);
      entry.label = entry.exact ? String(sum) : String(maxOwn || max) + '+';
      entry.visible = soft ? sum >= min : sum > 0;
      if (entry.visible) result.visibleCount++;
      result.options.push(entry);
    });
    return result;
  }

  /* Preisschritt: je Option eine eigene Facetten-Antwort (Map optionId -> facets|null); Zähler = count. */
  function evaluatePriceOptions(step, facetsByOption, minResults) {
    const min = Number(minResults) > 0 ? Number(minResults) : 1;
    const result = { options: [], visibleCount: 0, degraded: false };
    if (!step || !Array.isArray(step.options)) return result;
    const soft = step.mode === 'soft';
    step.options.forEach((opt) => {
      if (!opt) return;
      const facets = facetsByOption ? (facetsByOption instanceof Map ? facetsByOption.get(opt.id) : facetsByOption[opt.id]) : null;
      const entry = { id: opt.id, visible: true, count: null, label: null, exact: true, routing: !opt.price };
      if (!opt.price) {
        result.options.push(entry);
        result.visibleCount++;
        return;
      }
      if (!facets || typeof facets.count !== 'number') {
        result.degraded = true;
        result.options.push(entry);
        result.visibleCount++;
        return;
      }
      entry.count = facets.count;
      entry.label = String(facets.count);
      entry.visible = soft ? facets.count >= min : facets.count > 0;
      if (entry.visible) result.visibleCount++;
      result.options.push(entry);
    });
    return result;
  }

  /*
    Regel 3: weiche Filter von hinten nach vorn entfernen, bis count >= minResults.
    countFn(filters) -> number | null | Promise davon. null = unbekannt -> Abbruch.
  */
  async function relax(filters, minResults, countFn) {
    const min = Number(minResults) > 0 ? Number(minResults) : 1;
    let current = (filters || []).slice();
    const removed = [];
    let count = await countFn(current);
    let guard = 0;
    while (typeof count === 'number' && count < min && guard++ < 50) {
      let idx = -1;
      for (let i = current.length - 1; i >= 0; i--) {
        if (current[i].mode === 'soft') { idx = i; break; }
      }
      if (idx < 0) break;
      removed.push(current[idx]);
      current = current.slice(0, idx).concat(current.slice(idx + 1));
      count = await countFn(current);
    }
    return { filters: current, removed, count: typeof count === 'number' ? count : null };
  }

  /* Längster Restpfad je Schritt (Anzahl Schritte inkl. sich selbst) per DFS, Zyklen ignoriert. */
  function longestPath(steps) {
    const memo = Object.create(null);
    const visiting = Object.create(null);
    function targets(step) {
      const t = [];
      if (!step) return t;
      if (step.next) t.push(step.next);
      (step.options || []).forEach((o) => { if (o && o.next) t.push(o.next); });
      return uniq(t);
    }
    function rest(id) {
      if (!id || id === 'result' || !steps || !steps[id]) return 0;
      if (memo[id] != null) return memo[id];
      if (visiting[id]) return 0;
      visiting[id] = true;
      let best = 0;
      targets(steps[id]).forEach((t) => { const r = rest(t); if (r > best) best = r; });
      visiting[id] = false;
      memo[id] = 1 + best;
      return memo[id];
    }
    Object.keys(steps || {}).forEach(rest);
    return memo;
  }

  function progress(answered, currentStepId, longest) {
    const restLen = currentStepId && currentStepId !== 'result' && longest ? (longest[currentStepId] || 1) : 0;
    const total = answered + restLen;
    if (total <= 0) return 0;
    return Math.max(0, Math.min(1, answered / total));
  }

  /* Folgeschritt: option.next der ersten gewählten Option mit next, sonst step.next. */
  function nextStepId(step, optionIds) {
    if (!step) return 'result';
    for (let i = 0; i < (optionIds || []).length; i++) {
      const o = findOption(step, optionIds[i]);
      if (o && o.next) return o.next;
    }
    return step.next || 'result';
  }

  /* Einstieg: Options-IDs ab start "beantworten". null, wenn eine ID nicht passt. */
  function entryAnswers(config, ids) {
    const answers = [];
    let cur = config && config.start;
    for (let i = 0; i < (ids || []).length; i++) {
      const step = config.steps && config.steps[cur];
      const opt = findOption(step, ids[i]);
      if (!opt) return null;
      answers.push({ stepId: cur, optionIds: [ids[i]], labels: [opt.label || ids[i]], skipped: false, preset: true });
      cur = nextStepId(step, [ids[i]]);
    }
    return { answers, stepId: cur };
  }

  function priceLabel(gte, lte) {
    const a = gte != null && gte !== '' ? Number(gte) : null;
    const b = lte != null && lte !== '' ? Number(lte) : null;
    if (a != null && b != null) return a + '–' + b + ' €';
    if (b != null) return 'bis ' + b + ' €';
    if (a != null) return 'ab ' + a + ' €';
    return null;
  }

  /*
    Chips aus URL-Parametern (Ergebnisleiste ohne gespeicherten Zustand):
    Expansions-Werte ausblenden, wenn ein anderer Wert desselben Filters vorhanden ist.
  */
  /* Vergleicht nur die filter.*-Parameter zweier URLs/Query-Strings (Reihenfolge egal). */
  function sameFilterParams(a, b) {
    const norm = (s) => {
      const str = String(s || '');
      const q = str.indexOf('?') >= 0 ? str.slice(str.indexOf('?') + 1) : str;
      const out = [];
      new URLSearchParams(q).forEach((v, k) => { if (k.indexOf('filter.') === 0) out.push(k + '=' + v); });
      return out.sort().join('&');
    };
    return norm(a) === norm(b);
  }

  function chipsFromParams(params, expansions) {
    const groups = Object.create(null);
    const order = [];
    let gte = null;
    let lte = null;
    params.forEach((value, name) => {
      if (name === PARAM_PRICE_GTE) { gte = value; return; }
      if (name === PARAM_PRICE_LTE) { lte = value; return; }
      if (name.indexOf('filter.p.m.') !== 0) return;
      let key = name.slice('filter.p.m.'.length);
      if (key.indexOf('filter.') === 0) key = key.slice('filter.'.length);
      if (!groups[key]) { groups[key] = []; order.push(key); }
      if (groups[key].indexOf(value) < 0) groups[key].push(value);
    });
    const chips = [];
    order.forEach((key) => {
      const values = groups[key];
      const rules = expansions && expansions[key] ? expansions[key] : null;
      const shown = values.filter((v) => {
        if (values.length < 2) return true;
        if (rules && Array.isArray(rules['*']) && rules['*'].indexOf(v) >= 0) return false;
        if (!rules && /^Alle /.test(v)) return false;
        if (rules) {
          for (let i = 0; i < values.length; i++) {
            const w = values[i];
            if (w === v) continue;
            const expW = Array.isArray(rules[w]) ? rules[w] : [];
            const expV = Array.isArray(rules[v]) ? rules[v] : [];
            if (expW.indexOf(v) >= 0 && expV.indexOf(w) < 0) return false;
          }
        }
        return true;
      });
      shown.forEach((v) => chips.push({ key, value: v }));
    });
    const pl = priceLabel(gte, lte);
    if (pl) chips.push({ key: 'price', value: pl });
    return chips;
  }

  /* ------------------------------------------------------------------ */
  /* Infrastruktur                                                       */
  /* ------------------------------------------------------------------ */

  function shopRoot() {
    try {
      const r = win.Shopify && win.Shopify.routes && win.Shopify.routes.root;
      return normalizeRoot(r);
    } catch (e) { return '/'; }
  }

  function designMode() {
    try { return !!(win.Shopify && win.Shopify.designMode); } catch (e) { return false; }
  }

  function reducedMotion() {
    try { return !!(win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) { return false; }
  }

  /* Facetten-Client: Map url -> Promise, max. MAX_PARALLEL gleichzeitige Requests, Prefetch mit niedriger Priorität. */
  class FacetClient {
    constructor() {
      this.cache = new Map();
      this.queue = [];
      this.active = 0;
    }

    get(url, priority) {
      if (this.cache.has(url)) return this.cache.get(url);
      let resolve;
      const promise = new Promise((r) => { resolve = r; });
      const task = { url, resolve };
      this.cache.set(url, promise);
      if (priority) this.queue.unshift(task); else this.queue.push(task);
      this.pump();
      return promise;
    }

    prefetch(url) {
      if (this.cache.has(url)) return;
      if (this.active + this.queue.length >= MAX_PARALLEL * 2) return; // Prefetch-Flut vermeiden
      this.get(url, false);
    }

    pump() {
      while (this.active < MAX_PARALLEL && this.queue.length) {
        const task = this.queue.shift();
        this.active++;
        this.run(task.url).then((facets) => {
          if (facets === null) this.cache.delete(task.url); // später erneut versuchen
          task.resolve(facets);
        }).catch(() => {
          this.cache.delete(task.url);
          task.resolve(null);
        }).then(() => {
          this.active--;
          this.pump();
        });
      }
    }

    run(url) {
      if (typeof fetch !== 'function') return Promise.resolve(null);
      let controller = null;
      let timer = null;
      try {
        if (typeof AbortController === 'function') {
          controller = new AbortController();
          timer = setTimeout(() => controller.abort(), FETCH_TIMEOUT);
        }
      } catch (e) { controller = null; }
      const init = { credentials: 'same-origin', headers: { Accept: 'text/html' } };
      if (controller) init.signal = controller.signal;
      return fetch(url, init)
        .then((res) => (res && res.ok ? res.text() : null))
        .then((text) => {
          if (timer) clearTimeout(timer);
          const facets = parseFacets(text);
          if (!facets) warn('Facetten nicht ladbar:', url);
          return facets;
        })
        .catch((err) => {
          if (timer) clearTimeout(timer);
          warn('Facetten-Request fehlgeschlagen:', url, err && err.message);
          return null;
        });
    }
  }

  /* sessionStorage-Zustand (für Resume und Ergebnisleiste). */
  const Storage = {
    load() {
      try {
        const raw = win.sessionStorage && win.sessionStorage.getItem(STORAGE_KEY);
        if (!raw) return null;
        const data = JSON.parse(raw);
        return data && data.v === 1 && Array.isArray(data.answers) ? data : null;
      } catch (e) { return null; }
    },
    save(data) {
      try {
        if (!win.sessionStorage) return;
        win.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(Object.assign({ v: 1, ts: Date.now() }, data)));
      } catch (e) { /* privater Modus o. ä. */ }
    },
    clear() {
      try { win.sessionStorage && win.sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* nichts */ }
    },
  };

  /* Tracking: CustomEvent, dataLayer, Shopify.analytics – jeweils nur wenn vorhanden. */
  function emit(name, payload) {
    const data = payload || {};
    try {
      if (hasDOM) document.dispatchEvent(new CustomEvent('produktfinder:' + name, { detail: data, bubbles: true }));
    } catch (e) { warn('Event', name, e); }
    try {
      if (Array.isArray(win.dataLayer)) win.dataLayer.push(Object.assign({ event: 'produktfinder_' + name }, data));
    } catch (e) { warn('dataLayer', name, e); }
    try {
      const a = win.Shopify && win.Shopify.analytics;
      if (a && typeof a.publish === 'function') a.publish('produktfinder_' + name, data);
    } catch (e) { warn('Shopify.analytics', name, e); }
  }

  /* Config einmal pro Seite lesen und vorbereiten. */
  let configCache;
  function loadConfig() {
    if (configCache !== undefined) return configCache;
    configCache = null;
    try {
      const node = hasDOM ? document.getElementById(CONFIG_ID) : null;
      if (!node) { warn('Config-Snippet #' + CONFIG_ID + ' fehlt.'); return null; }
      const cfg = JSON.parse(node.textContent || '');
      if (!cfg || typeof cfg !== 'object' || !cfg.steps || !cfg.start || !cfg.steps[cfg.start]) {
        warn('Config ungültig (start/steps fehlen).');
        return null;
      }
      cfg.expansions = cfg.expansions || {};
      cfg.entry_points = cfg.entry_points || {};
      cfg.longest = longestPath(cfg.steps);
      configCache = cfg;
    } catch (e) {
      warn('Config nicht lesbar:', e && e.message);
      configCache = null;
    }
    return configCache;
  }

  const facetClient = new FacetClient();

  /* DOM-Helfer: el('div', { class: 'x', onclick: fn }, 'text', child, ...) */
  function el(tag, attrs) {
    const node = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach((k) => {
        const v = attrs[k];
        if (v === null || v === undefined || v === false) return;
        if (k === 'class') node.className = v;
        else if (k === 'text') node.textContent = v;
        else if (k === 'html') node.innerHTML = v;
        else if (k.indexOf('on') === 0 && typeof v === 'function') node.addEventListener(k.slice(2), v);
        else if (v === true) node.setAttribute(k, '');
        else node.setAttribute(k, String(v));
      });
    }
    for (let i = 2; i < arguments.length; i++) {
      const c = arguments[i];
      if (c === null || c === undefined || c === false) continue;
      if (Array.isArray(c)) c.forEach((x) => { if (x) node.appendChild(typeof x === 'string' ? document.createTextNode(x) : x); });
      else node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
    }
    return node;
  }

  function icon(id, cls) {
    if (!id || !document.getElementById('pf-icon-' + id)) return null;
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', cls || 'pf-icon');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('focusable', 'false');
    const use = document.createElementNS('http://www.w3.org/2000/svg', 'use');
    use.setAttribute('href', '#pf-icon-' + id);
    use.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#pf-icon-' + id);
    svg.appendChild(use);
    return svg;
  }

  function clear(node) { while (node && node.firstChild) node.removeChild(node.firstChild); }

  function pageOrigin() {
    try { return location.pathname + location.search; } catch (e) { return '/'; }
  }

  /* ------------------------------------------------------------------ */
  /* <produkt-finder>                                                    */
  /* ------------------------------------------------------------------ */

  function defineElements() {
    if (customElements.get('produkt-finder')) return;

    class ProduktFinder extends HTMLElement {
      constructor() {
        super();
        this._token = 0;
        this._timers = [];
        this._started = false;
        this._answering = false;
      }

      connectedCallback() {
        try { this.init(); } catch (e) { warn('Init fehlgeschlagen:', e); this.hideAll(); }
      }

      disconnectedCallback() {
        this.clearTimers();
        this.unbindGlobal();
      }

      /* ---------- Einstellungen / Einstieg ---------- */

      readSettings() {
        const d = this.dataset;
        const min = parseInt(d.minResults, 10);
        this.settings = {
          startBranch: d.startBranch || 'auto',
          layout: d.layout === 'compact' ? 'compact' : 'full',
          minResults: min > 0 ? min : 0,
          showCounts: d.showCounts !== 'false',
          autoRedirect: d.autoRedirect !== 'false', // nach der letzten Antwort direkt zur Ergebnisseite
          inModal: d.inModal === 'true', // Instanz im Header-Modal (sections/produktfinder-modal.liquid)
          handle: d.collectionHandle || '',
          buttonLabel: d.buttonLabel || 'Produkte anzeigen',
          compactLabel: d.compactLabel || 'Finde dein Produkt in 3 Fragen',
          sectionId: d.sectionId || '',
        };
      }

      init() {
        this.readSettings();
        this.config = loadConfig();
        this.root = shopRoot();
        this.reduced = reducedMotion();
        this.answers = [];
        this.stepId = null;
        this.pending = [];
        this.lastEval = null;
        this.entry = this.settings.handle ? 'collection:' + this.settings.handle : 'home';
        this.section = this.closest('[data-produktfinder-section]');
        if (!this.config) { this.hideAll(); return; }
        const cfg = this.config;
        this.minResults = this.settings.minResults || Number(cfg.min_results) || 4;

        let ids;
        if (this.settings.startBranch === 'auto') {
          if (this.settings.handle) {
            ids = cfg.entry_points[this.settings.handle];
            // null = für diese Kollektion bewusst ausgeblendet; kein Eintrag = ganz normal bei „Wofür suchst du etwas?“ starten
            if (ids === null) { this.noEntry(); return; }
            if (!Array.isArray(ids)) ids = [];
          } else {
            ids = [];
          }
        } else {
          ids = [this.settings.startBranch];
        }
        const entry = entryAnswers(cfg, ids);
        if (!entry) { warn('Einstieg ungültig:', ids); this.noEntry(); return; }
        this.initialAnswers = entry.answers;
        this.initialStepId = entry.stepId;
        this.answers = entry.answers.slice();

        this.buildShell();
        this.show();
        this.bindGlobal();

        // Resume: ein Finder auf der Seite selbst hat Vorrang; das Modal übernimmt nur, wenn es keinen gibt.
        const inlineFinder = this.settings.inModal && document.querySelector('produkt-finder:not([data-in-modal])');
        if (this.isResumeRequested() && !inlineFinder && this.tryResume()) {
          if (this.settings.inModal) this._resumedInModal = true; // setupModal() öffnet dann das Modal
          return;
        }
        this.showStep(this.initialStepId, { focus: false });
        if (this.settings.layout === 'full' && !this.settings.inModal) this.markStarted(this.entry);
      }

      markStarted(entry) {
        if (this._started) return;
        this._started = true;
        emit('start', { branch: this.branchOf(this.stepId), entry: entry || this.entry });
      }

      branchOf(stepId) {
        const s = this.config && this.config.steps[stepId];
        return (s && s.branch) || 'start';
      }

      noEntry() {
        if (designMode()) {
          clear(this);
          this.appendChild(el('p', { class: 'pf-notice text-sm', text: 'Für diese Kollektion ist kein Einstieg definiert (Produktfinder wird im Shop ausgeblendet).' }));
          this.show();
        } else {
          this.hideAll();
        }
      }

      hideAll() {
        this.hidden = true;
        if (this.section) this.section.hidden = true;
      }

      show() {
        this.hidden = false;
        if (this.section) this.section.hidden = false;
      }

      isResumeRequested() {
        try { return location.hash === '#produktfinder-resume'; } catch (e) { return false; }
      }

      /* ---------- Hülle ---------- */

      buildShell() {
        clear(this);
        const compact = this.settings.layout === 'compact';
        this.classList.add('pf--' + this.settings.layout);
        if (compact) {
          this.compactBtn = el('button', {
            type: 'button', class: 'btn btn--outline btn--small pf-compact__btn', 'aria-expanded': 'false',
            onclick: () => this.toggleCompact(),
          }, 'Los geht’s');
          this.compactBar = el('div', { class: 'pf-compact' },
            icon('sparkles', 'pf-icon pf-compact__icon'),
            el('span', { class: 'pf-compact__label', text: this.settings.compactLabel }),
            this.compactBtn);
          this.appendChild(this.compactBar);
        }
        this.progressEl = el('div', { class: 'pf-progress', role: 'progressbar', 'aria-valuemin': '0', 'aria-valuemax': '100', 'aria-valuenow': '0', 'aria-label': 'Fortschritt' },
          el('div', { class: 'pf-progress__bar' }));
        this.pathEl = el('div', { class: 'pf-path', role: 'navigation', 'aria-label': 'Deine Antworten' });
        this.backBtn = el('button', { type: 'button', class: 'pf-back', onclick: () => this.goBack() }, icon('back', 'pf-icon pf-icon--sm'), 'Zurück');
        this.navEl = el('div', { class: 'pf-nav' }, this.pathEl, this.backBtn);
        this.stage = el('div', { class: 'pf-stage' });
        this.live = el('div', { class: 'pf-live visually-hidden', 'aria-live': 'polite', 'aria-atomic': 'true' });
        this.body = el('div', { class: 'pf-body', hidden: compact }, this.progressEl, this.navEl, this.stage, this.live);
        this.appendChild(this.body);
      }

      toggleCompact(force) {
        if (!this.body || !this.compactBtn) return;
        const open = typeof force === 'boolean' ? force : this.body.hidden;
        this.body.hidden = !open;
        this.classList.toggle('pf--open', open);
        this.compactBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        this.compactBtn.textContent = open ? 'Schließen' : 'Los geht’s';
        if (open) {
          this.markStarted();
          this.focusQuestion();
        }
      }

      /* ---------- Schritt anzeigen ---------- */

      derived() {
        return deriveFilters(this.config, this.answers, '');
      }

      showStep(stepId, opts) {
        const o = opts || {};
        const cfg = this.config;
        const step = cfg.steps[stepId];
        if (!step || stepId === 'result') { this.showResult(o); return; }
        if ((o.depth || 0) > 30) { warn('Zu viele automatische Sprünge, zeige Ergebnis.'); this.showResult(o); return; }

        this.stepId = stepId;
        this.pending = Array.isArray(o.preselect) ? o.preselect.slice() : [];
        this.lastEval = null;
        this._answering = false;
        const token = ++this._token;
        const derived = this.derived();
        const view = this.renderStep(step, stepId);
        this.view = view;
        this.swapStage(view.root, o);
        this.updateNav();
        this.announce(step.question);

        if (!step.filter) { view.setBusy(false); return; }
        if (!derived.collection) { warn('Keine Zielkollektion vor Schritt', stepId); view.setBusy(false); return; }

        const base = { root: this.root, collection: derived.collection, filters: derived.filters, price: derived.price };
        let evaluation;
        if (step.filter === 'price') {
          evaluation = Promise.all(step.options.map((opt) => {
            if (!opt || !opt.price) return Promise.resolve(null);
            const price = Object.assign({}, derived.price || {});
            if (opt.price.gte != null) price.gte = price.gte != null ? Math.max(price.gte, opt.price.gte) : opt.price.gte;
            if (opt.price.lte != null) price.lte = price.lte != null ? Math.min(price.lte, opt.price.lte) : opt.price.lte;
            return facetClient.get(facetUrl(Object.assign({}, base, { price })), true);
          })).then((list) => {
            const map = new Map();
            step.options.forEach((opt, i) => { if (opt) map.set(opt.id, list[i]); });
            return evaluatePriceOptions(step, map, this.minResults);
          });
        } else {
          evaluation = facetClient.get(facetUrl(base), true)
            .then((facets) => evaluateOptions(step, facets, cfg.expansions, this.minResults));
        }

        evaluation.then((ev) => {
          if (token !== this._token) return;
          this.lastEval = ev;
          view.apply(ev, this.settings.showCounts);
          if (ev.visibleCount <= 1 && !ev.degraded && !o.noSkip) { this.autoSkip(step, stepId, o); return; }
          view.setBusy(false);
          this.announce(step.question + '. ' + ev.visibleCount + ' Optionen.');
        }).catch((e) => {
          warn('Auswertung fehlgeschlagen:', e);
          if (token === this._token) view.setBusy(false);
        });
      }

      autoSkip(step, stepId, opts) {
        const next = step.next || 'result';
        // Ohne Klick der Kundin darf ein Einstieg (vorbelegte Antworten einer Kollektionsseite) nie bis zum Ergebnis
        // durchrutschen – sonst würde die Seite sofort weitergeleitet. Dann die letzte Vorbelegung zurücknehmen und
        // diesen Schritt als offene Frage zeigen.
        if (next === 'result' && !this._interacted) {
          while (this.answers.length && this.answers[this.answers.length - 1].auto) this.answers.pop();
          const last = this.answers.pop();
          if (last) {
            this.initialAnswers = this.answers.slice();
            this.initialStepId = last.stepId;
            this.showStep(last.stepId, { focus: false, depth: (opts.depth || 0) + 1, noSkip: true });
            return;
          }
          if (this.view && this.view.setBusy) this.view.setBusy(false);
          return;
        }
        this.answers.push({ stepId, optionIds: [], labels: [], skipped: true, auto: true });
        this.showStep(next, { focus: opts.focus, depth: (opts.depth || 0) + 1 });
      }

      renderStep(step, stepId) {
        const multi = Number(step.multi) > 1 ? Number(step.multi) : 1;
        const qid = 'pf-q-' + (this.settings.sectionId || 'x') + '-' + stepId;
        const question = el('h3', { class: 'pf-question h4', id: qid, tabindex: '-1', text: step.question || '' });
        const hint = step.hint ? el('p', { class: 'pf-hint text-sm', text: step.hint }) : null;
        const help = step.help && step.help.text
          ? el('details', { class: 'pf-help' },
            el('summary', { class: 'pf-help__summary text-sm' }, icon('info', 'pf-icon pf-icon--sm'), el('span', { text: step.help.title || 'Unsicher?' })),
            el('p', { class: 'pf-help__text text-sm', text: step.help.text }))
          : null;
        const hasIcons = (step.options || []).some((o) => o && o.icon && document.getElementById('pf-icon-' + o.icon));
        const list = el('div', {
          class: 'pf-options' + (hasIcons ? ' pf-options--icons' : ''),
          role: multi > 1 ? 'group' : 'radiogroup',
          'aria-labelledby': qid,
          'aria-busy': step.filter ? 'true' : 'false',
        });
        const buttons = new Map();
        (step.options || []).forEach((opt) => {
          if (!opt || !opt.id) return;
          const checked = this.pending.indexOf(opt.id) >= 0;
          const btn = el('button', {
            type: 'button',
            class: 'pf-option' + (checked ? ' pf-option--selected' : ''),
            role: multi > 1 ? 'checkbox' : 'radio',
            'aria-checked': checked ? 'true' : 'false',
            'data-option': opt.id,
            onclick: () => this.onOptionClick(step, opt, btn, list, multi),
            onmouseenter: () => this.prefetchFor(step, opt),
            onfocus: () => this.prefetchFor(step, opt),
            onkeydown: (e) => this.onOptionKey(e, list),
          },
          icon(opt.icon, 'pf-icon pf-option__icon'),
          el('span', { class: 'pf-option__body' },
            el('span', { class: 'pf-option__label', text: opt.label || opt.id }),
            opt.sub ? el('span', { class: 'pf-option__sub text-sm', text: opt.sub }) : null),
          el('span', { class: 'pf-option__count' }),
          el('span', { class: 'pf-option__check', 'aria-hidden': 'true' }, icon('check', 'pf-icon pf-icon--sm')));
          buttons.set(opt.id, btn);
          list.appendChild(btn);
        });

        const actions = el('div', { class: 'pf-actions' });
        let nextBtn = null;
        if (multi > 1) {
          nextBtn = el('button', {
            type: 'button', class: 'btn btn--primary btn--small pf-next', disabled: this.pending.length === 0,
            onclick: () => { if (this.pending.length) this.answer(step, this.pending.slice(), false); },
          }, 'Weiter');
          actions.appendChild(nextBtn);
        }
        if (step.skip) {
          actions.appendChild(el('button', {
            type: 'button', class: 'btn btn--outline btn--small pf-skip',
            onclick: () => this.answer(step, [], true),
          }, step.skip));
        }
        if (multi > 1 && !step.hint) actions.appendChild(el('span', { class: 'pf-actions__hint text-sm', text: 'Bis zu ' + multi + ' Antworten möglich' }));

        const root = el('div', { class: 'pf-step', 'data-step': stepId }, question, hint, help, list, actions.childNodes.length ? actions : null);
        const showCountsDefault = this.settings.showCounts;
        return {
          root,
          question,
          list,
          nextBtn,
          buttons,
          setBusy(busy) { list.setAttribute('aria-busy', busy ? 'true' : 'false'); },
          apply(ev, showCounts) {
            const sc = typeof showCounts === 'boolean' ? showCounts : showCountsDefault;
            ev.options.forEach((o) => {
              const b = buttons.get(o.id);
              if (!b) return;
              b.hidden = !o.visible;
              const c = b.querySelector('.pf-option__count');
              if (c) {
                clear(c);
                if (sc && o.label != null) {
                  c.appendChild(el('span', { text: o.label }));
                  c.appendChild(el('span', { class: 'visually-hidden', text: ' Produkte' }));
                }
              }
            });
          },
        };
      }

      /* ---------- Interaktion ---------- */

      onOptionClick(step, opt, btn, list, multi) {
        try {
          if (list.getAttribute('aria-busy') === 'true' || this._answering) return;
          if (multi === 1) {
            this._answering = true;
            list.querySelectorAll('.pf-option').forEach((b) => { b.setAttribute('aria-checked', 'false'); b.classList.remove('pf-option--selected'); });
            btn.setAttribute('aria-checked', 'true');
            btn.classList.add('pf-option--selected');
            this.later(() => this.answer(step, [opt.id], false), this.reduced ? 0 : CONFIRM_DELAY);
            return;
          }
          const idx = this.pending.indexOf(opt.id);
          if (idx >= 0) {
            this.pending.splice(idx, 1);
            btn.setAttribute('aria-checked', 'false');
            btn.classList.remove('pf-option--selected');
          } else if (this.pending.length < multi) {
            this.pending.push(opt.id);
            btn.setAttribute('aria-checked', 'true');
            btn.classList.add('pf-option--selected');
          } else {
            this.announce('Maximal ' + multi + ' Antworten möglich. Wähle zuerst eine ab.');
            return;
          }
          if (this.view && this.view.nextBtn) this.view.nextBtn.disabled = this.pending.length === 0;
        } catch (e) { warn('Klick:', e); }
      }

      onOptionKey(e, list) {
        const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1, Home: 'first', End: 'last' };
        const dir = keys[e.key];
        if (dir === undefined) return;
        const items = Array.prototype.filter.call(list.querySelectorAll('.pf-option'), (b) => !b.hidden);
        if (!items.length) return;
        const cur = items.indexOf(e.target.closest('.pf-option'));
        let next;
        if (dir === 'first') next = 0;
        else if (dir === 'last') next = items.length - 1;
        else next = (cur + dir + items.length) % items.length;
        e.preventDefault();
        items[next].focus();
      }

      prefetchFor(step, opt) {
        try {
          if (!opt || Number(step.multi) > 1) return;
          const next = opt.next || step.next || 'result';
          const ns = this.config.steps[next];
          if (next !== 'result' && (!ns || !ns.filter || ns.filter === 'price')) return;
          const d = deriveFilters(this.config, this.answers.concat([{ stepId: this.stepId, optionIds: [opt.id], skipped: false }]), '');
          if (!d.collection) return;
          facetClient.prefetch(facetUrl({ root: this.root, collection: d.collection, filters: d.filters, price: d.price }));
        } catch (e) { /* Prefetch ist optional */ }
      }

      answer(step, optionIds, skipped) {
        this._interacted = true; // erst ab hier darf der Finder automatisch weiterleiten
        try {
          const stepId = this.stepId;
          const labels = optionIds.map((id) => { const o = findOption(step, id); return (o && o.label) || id; });
          this.answers.push({ stepId, optionIds, labels, skipped: !!skipped, question: step.question });
          let count = null;
          if (this.lastEval && optionIds.length) {
            count = 0;
            this.lastEval.options.forEach((o) => { if (optionIds.indexOf(o.id) >= 0 && typeof o.count === 'number') count += o.count; });
          }
          this.persist();
          emit('step', { branch: step.branch, step: stepId, question: step.question, options: optionIds, labels, skipped: !!skipped, count });
          const next = skipped ? (step.next || 'result') : nextStepId(step, optionIds);
          this.showStep(next, { focus: true });
        } catch (e) { warn('Antwort:', e); }
      }

      persist(extra) {
        const d = this.derived();
        const base = {
          origin: pageOrigin(),
          sectionId: this.settings.sectionId,
          entry: this.entry,
          answers: this.answers,
          target: d.collection,
          url: null,
          count: null,
          relaxed: [],
        };
        Storage.save(Object.assign(base, extra || {}));
      }

      /* ---------- Ergebnis ---------- */

      showResult(opts) {
        const o = opts || {};
        this.stepId = 'result';
        this.pending = [];
        const token = ++this._token;
        const d = this.derived();
        const collection = d.collection;
        const view = this.renderResult(d);
        this.view = view;
        this.swapStage(view.root, o);
        this.updateNav();

        if (!collection) {
          warn('Kein Ziel für das Ergebnis.');
          view.update({ count: null, url: this.root + 'collections/all', removed: [] });
          return;
        }
        const countFn = (filters) => facetClient.get(facetUrl({ root: this.root, collection, filters, price: d.price }), true)
          .then((facets) => (facets && typeof facets.count === 'number' ? facets.count : null));

        // Direktweiterleitung: im Theme-Editor nie (würde das Bearbeiten unterbrechen), sonst laut Einstellung.
        const redirect = this.settings.autoRedirect && !designMode() && this._interacted;
        if (redirect) view.setLoading('Deine Auswahl wird zusammengestellt …');

        relax(d.filters, this.minResults, countFn).then((res) => {
          if (token !== this._token) return;
          const url = buildUrl({ root: this.root, collection, filters: res.filters, price: d.price });
          const removedLabels = res.removed.map((f) => this.labelsForKey(f.key)).reduce((a, b) => a.concat(b), []);
          view.update({ count: res.count, url, removed: res.removed, removedLabels, collectionUrl: this.root + 'collections/' + encodeURIComponent(collection) });
          this.persist({ url, count: res.count, relaxed: removedLabels, target: collection });
          this.announce(typeof res.count === 'number' ? res.count + ' passende Produkte.' : 'Ergebnis bereit.');
          emit('complete', {
            branch: this.branchOf(this.lastStepId()),
            collection,
            url,
            count: res.count,
            path: this.answers.filter((a) => !a.auto).map((a) => ({ step: a.stepId, options: a.optionIds })),
            relaxed: res.removed.map((f) => f.key),
          });
          if (redirect) this.navigateTo(url);
        }).catch((e) => {
          warn('Ergebnis:', e);
          if (token !== this._token) return;
          const url = buildUrl({ root: this.root, collection, filters: d.filters, price: d.price });
          view.update({ count: null, url, removed: [] });
          if (redirect) this.navigateTo(url);
        });
      }

      /* Wechselt zur Ergebnisseite; das Panel bleibt als Rückfall sichtbar, falls der Browser die Navigation blockiert. */
      navigateTo(url) {
        try { win.location.assign(url); } catch (e) { warn('Weiterleitung fehlgeschlagen:', e); }
      }

      lastStepId() {
        for (let i = this.answers.length - 1; i >= 0; i--) if (!this.answers[i].auto) return this.answers[i].stepId;
        return this.config.start;
      }

      /* Labels der Antworten, die zu einem Filter-Key beigetragen haben (für den Relax-Hinweis). */
      labelsForKey(key) {
        const out = [];
        this.answers.forEach((a) => {
          const step = this.config.steps[a.stepId];
          if (!step || a.skipped) return;
          a.optionIds.forEach((id) => {
            const opt = findOption(step, id);
            if (!opt) return;
            const hit = optionValueGroups(step, opt, this.config.expansions).some((g) => g.key === key);
            if (hit && out.indexOf(opt.label || id) < 0) out.push(opt.label || id);
          });
        });
        return out;
      }

      renderResult(d) {
        const qid = 'pf-q-' + (this.settings.sectionId || 'x') + '-result';
        const question = el('h3', { class: 'pf-question h4', id: qid, tabindex: '-1', text: 'Deine Auswahl' });
        const chips = el('ul', { class: 'pf-chips', 'aria-label': 'Deine Antworten' });
        this.answers.forEach((a) => {
          if (a.auto || a.skipped) return;
          a.labels.forEach((label) => chips.appendChild(el('li', { class: 'pf-chip pf-chip--static', text: label })));
        });
        const count = el('p', { class: 'pf-result__count', text: 'Wir zählen deine Treffer …' });
        const notice = el('p', { class: 'pf-result__notice text-sm', hidden: true });
        const link = el('a', { class: 'btn btn--primary pf-result__link', href: '#', 'aria-disabled': 'true' }, this.settings.buttonLabel);
        const restart = el('button', { type: 'button', class: 'btn btn--outline btn--small pf-restart', onclick: () => this.restart() }, icon('restart', 'pf-icon pf-icon--sm'), 'Neu starten');
        const actions = el('div', { class: 'pf-actions pf-actions--result' }, link, restart);
        const root = el('div', { class: 'pf-result', 'aria-busy': 'true' }, question, chips.childNodes.length ? chips : null, count, notice, actions);
        const buttonLabel = this.settings.buttonLabel;
        return {
          root,
          question,
          /* Kurzer Ladezustand vor der Direktweiterleitung: nur Text, keine Buttons. */
          setLoading(text) {
            count.textContent = text;
            actions.hidden = true;
          },
          update(res) {
            root.setAttribute('aria-busy', 'false');
            actions.hidden = false;
            link.removeAttribute('aria-disabled');
            if (typeof res.count === 'number' && res.count === 0) {
              count.textContent = 'Für diese Kombination haben wir gerade nichts – schau dir die ganze Kategorie an.';
              link.href = res.collectionUrl || res.url;
              link.textContent = 'Zur Kategorie';
            } else if (typeof res.count === 'number') {
              count.textContent = res.count + (res.count === 1 ? ' passendes Produkt' : ' passende Produkte');
              link.href = res.url;
              link.textContent = buttonLabel;
            } else {
              count.textContent = 'Deine Empfehlung ist bereit.';
              link.href = res.url;
              link.textContent = buttonLabel;
            }
            if (res.removedLabels && res.removedLabels.length) {
              const names = res.removedLabels.map((l) => '„' + l + '“').join(', ');
              notice.textContent = 'Damit du mehr Auswahl hast, haben wir ' + names + ' weggelassen.';
              notice.hidden = false;
            } else {
              notice.hidden = true;
            }
          },
        };
      }

      /* ---------- Navigation ---------- */

      updateNav() {
        const cfg = this.config;
        const visible = [];
        this.answers.forEach((a, i) => { if (!a.auto) visible.push({ a, i }); });
        clear(this.pathEl);
        visible.forEach((item) => {
          const step = cfg.steps[item.a.stepId] || {};
          const label = item.a.skipped ? (step.skip || 'Übersprungen') : item.a.labels.join(' + ');
          this.pathEl.appendChild(el('button', {
            type: 'button', class: 'pf-chip', title: step.question || '', onclick: () => this.goTo(item.i),
          }, label));
        });
        this.backBtn.hidden = visible.length === 0;
        this.navEl.hidden = visible.length === 0 && this.stepId !== 'result';
        const answered = visible.length;
        const p = this.stepId === 'result' ? 1 : progress(answered, this.stepId, cfg.longest);
        const pct = Math.round(p * 100);
        this.progressEl.setAttribute('aria-valuenow', String(pct));
        this.progressEl.style.setProperty('--pf-progress', pct + '%');
      }

      goTo(index) {
        this._interacted = true;
        try {
          if (index < 0 || index >= this.answers.length) return;
          const target = this.answers[index];
          this.answers = this.answers.slice(0, index);
          this.persist();
          this.showStep(target.stepId, { focus: true, preselect: target.skipped ? [] : target.optionIds });
        } catch (e) { warn('Sprung:', e); }
      }

      goBack() {
        for (let i = this.answers.length - 1; i >= 0; i--) {
          if (!this.answers[i].auto) { this.goTo(i); return; }
        }
      }

      restart() {
        try {
          const branch = this.branchOf(this.lastStepId());
          this.answers = this.initialAnswers.slice();
          Storage.clear();
          this.showStep(this.initialStepId, { focus: true });
          emit('restart', { branch, entry: this.entry });
        } catch (e) { warn('Neustart:', e); }
      }

      /* ---------- Darstellung / a11y ---------- */

      swapStage(node, opts) {
        this.clearTimers();
        clear(this.stage);
        this.stage.appendChild(node);
        if (opts && opts.focus) this.focusQuestion();
      }

      focusQuestion() {
        const q = this.stage && this.stage.querySelector('.pf-question');
        if (!q) return;
        try { q.focus({ preventScroll: true }); } catch (e) { q.focus(); }
        try {
          const top = this.getBoundingClientRect().top;
          if (top < 0) win.scrollTo({ top: win.scrollY + top - 90, behavior: this.reduced ? 'auto' : 'smooth' });
        } catch (e) { /* nichts */ }
      }

      announce(text) {
        if (!this.live) return;
        this.live.textContent = '';
        this.later(() => { this.live.textContent = text; }, 60);
      }

      later(fn, ms) {
        const t = setTimeout(() => {
          this._timers = this._timers.filter((x) => x !== t);
          try { fn(); } catch (e) { warn(e); }
        }, ms);
        this._timers.push(t);
      }

      clearTimers() {
        this._timers.forEach((t) => clearTimeout(t));
        this._timers = [];
      }

      /* ---------- Resume / globale Events ---------- */

      tryResume() {
        try {
          const state = Storage.load();
          if (!state || !state.answers.length) return false;
          if (state.sectionId && state.sectionId !== this.settings.sectionId
            && document.querySelector('produkt-finder[data-section-id="' + state.sectionId + '"]')) return false;
          const cfg = this.config;
          const valid = state.answers.every((a) => cfg.steps[a.stepId]
            && (a.skipped || (Array.isArray(a.optionIds) && a.optionIds.every((id) => findOption(cfg.steps[a.stepId], id)))));
          if (!valid) return false;
          let idx = -1;
          for (let i = state.answers.length - 1; i >= 0; i--) if (!state.answers[i].auto) { idx = i; break; }
          if (idx < 0) return false;
          const last = state.answers[idx];
          this.answers = state.answers.slice(0, idx);
          this.entry = 'resume';
          if (this.settings.layout === 'compact') this.toggleCompact(true);
          this.showStep(last.stepId, { focus: true, preselect: last.skipped ? [] : last.optionIds });
          this._started = true;
          emit('start', { branch: this.branchOf(last.stepId), entry: 'resume' });
          this.later(() => { try { this.scrollIntoView({ block: 'start', behavior: this.reduced ? 'auto' : 'smooth' }); } catch (e) { /* nichts */ } }, 50);
          return true;
        } catch (e) {
          warn('Resume:', e);
          return false;
        }
      }

      bindGlobal() {
        if (this._onHash) return;
        this._onHash = () => {
          try {
            const h = location.hash;
            if (h === '#produktfinder-resume') this.tryResume();
            else if (h === '#produktfinder') {
              this.restart();
              if (this.settings.layout === 'compact') this.toggleCompact(true);
            }
          } catch (e) { warn(e); }
        };
        this._onSelect = (e) => {
          try {
            const id = e && e.detail && e.detail.sectionId;
            if (id && id === this.settings.sectionId && this.settings.layout === 'compact') this.toggleCompact(true);
          } catch (err) { /* nichts */ }
        };
        win.addEventListener('hashchange', this._onHash);
        document.addEventListener('shopify:section:select', this._onSelect);
      }

      unbindGlobal() {
        if (this._onHash) win.removeEventListener('hashchange', this._onHash);
        if (this._onSelect) document.removeEventListener('shopify:section:select', this._onSelect);
        this._onHash = null;
        this._onSelect = null;
      }
    }

    customElements.define('produkt-finder', ProduktFinder);

    /* ---------------------------------------------------------------- */
    /* <finder-result-bar>                                               */
    /* ---------------------------------------------------------------- */

    if (!customElements.get('finder-result-bar')) {
      class FinderResultBar extends HTMLElement {
        connectedCallback() {
          try { this.init(); } catch (e) { warn('Ergebnisleiste:', e); this.hidden = true; }
        }

        init() {
          let search = '';
          try { search = location.search || ''; } catch (e) { search = ''; }
          const params = new URLSearchParams(search);
          if (params.get('finder') !== '1') { this.hidden = true; return; }

          const handle = this.dataset.collection || '';
          const prefix = this.dataset.prefix || 'Deine Empfehlung:';
          const changeLabel = this.dataset.changeLabel || 'Antworten ändern';
          const restartLabel = this.dataset.restartLabel || 'Neu starten';
          const state = Storage.load();
          // Gespeicherter Zustand zählt nur, wenn er zu dieser Ergebnisseite gehört (gleiche Kollektion, gleiche Filter);
          // sonst (geteilter Link, alter Zustand aus einem anderen Durchlauf) kommen die Chips aus der URL.
          const stateMatches = !!(state && state.target && state.target === handle && state.url && sameFilterParams(state.url, search));
          const cfg = loadConfig();
          const root = shopRoot();

          let chips = [];
          if (stateMatches) {
            state.answers.forEach((a) => {
              if (a.auto || a.skipped || !Array.isArray(a.labels)) return;
              a.labels.forEach((l) => { if (chips.indexOf(l) < 0) chips.push(l); });
            });
            const gte = params.get(PARAM_PRICE_GTE);
            const lte = params.get(PARAM_PRICE_LTE);
            const pl = priceLabel(gte, lte);
            if (pl && chips.indexOf(pl) < 0 && !chips.some((c) => /€/.test(c))) chips.push(pl);
          }
          if (!chips.length) chips = chipsFromParams(params, cfg ? cfg.expansions : null).map((c) => c.value);

          clear(this);
          const list = el('ul', { class: 'pfr-chips', 'aria-label': 'Deine Antworten' });
          chips.forEach((c) => list.appendChild(el('li', { class: 'pfr-chip', text: c })));
          const links = el('div', { class: 'pfr-links' });
          if (stateMatches && state.origin) {
            links.appendChild(el('a', { class: 'pfr-link', href: state.origin + '#produktfinder-resume' }, icon('back', 'pf-icon pf-icon--sm'), changeLabel));
          }
          const restartHref = (stateMatches && state.origin ? state.origin : root) + '#produktfinder';
          links.appendChild(el('a', { class: 'pfr-link', href: restartHref }, icon('restart', 'pf-icon pf-icon--sm'), restartLabel));

          const row = el('div', { class: 'pfr-row' },
            el('span', { class: 'pfr-prefix' }, icon('sparkles', 'pf-icon pf-icon--sm'), el('span', { text: prefix })),
            chips.length ? list : null,
            links);
          this.appendChild(row);

          if (stateMatches && Array.isArray(state.relaxed) && state.relaxed.length) {
            const names = state.relaxed.map((l) => '„' + l + '“').join(', ');
            this.appendChild(el('p', { class: 'pfr-notice text-sm', text: 'Damit du mehr Auswahl hast, haben wir ' + names + ' weggelassen.' }));
          }
          this.hidden = false;
        }
      }
      customElements.define('finder-result-bar', FinderResultBar);
    }
  }

  /* ------------------------------------------------------------------ */
  /* Export + Start                                                      */
  /* ------------------------------------------------------------------ */

  win.Produktfinder = {
    paramName,
    applyExpansions,
    optionValueGroups,
    deriveFilters,
    buildUrl,
    facetUrl,
    parseFacets,
    evaluateOptions,
    evaluatePriceOptions,
    relax,
    longestPath,
    progress,
    nextStepId,
    entryAnswers,
    priceLabel,
    chipsFromParams,
    emit,
    FacetClient,
    constants: { PARAM_META, PARAM_GENDER, PARAM_PRICE, PARAM_PRICE_GTE, PARAM_PRICE_LTE, STORAGE_KEY, FACET_SECTION },
  };

  /* Header-Modal: <dialog data-produktfinder-modal> aus sections/produktfinder-modal.liquid, geöffnet über [data-produktfinder-open]. */
  function setupModal() {
    const dialog = document.querySelector('dialog[data-produktfinder-modal]');
    const triggers = Array.prototype.slice.call(document.querySelectorAll('[data-produktfinder-open]'));
    if (!dialog || typeof dialog.showModal !== 'function') {
      triggers.forEach((t) => { t.hidden = true; });
      return null;
    }
    const finder = () => dialog.querySelector('produkt-finder');
    const open = (entry) => {
      if (dialog.open) return;
      try { dialog.showModal(); } catch (e) { warn('Modal konnte nicht geöffnet werden:', e); return; }
      document.documentElement.classList.add('pf-modal-open');
      const f = finder();
      if (f && typeof f.markStarted === 'function') f.markStarted(entry || 'modal');
      const q = dialog.querySelector('.pf-question');
      if (q && typeof q.focus === 'function') { try { q.focus({ preventScroll: true }); } catch (e) { /* nichts */ } }
      emit('modal_open', { entry: entry || 'modal' });
    };
    const close = () => { if (dialog.open) dialog.close(); };
    dialog.addEventListener('close', () => document.documentElement.classList.remove('pf-modal-open'));
    dialog.addEventListener('click', (ev) => { if (ev.target === dialog) close(); }); // Klick auf den Hintergrund
    Array.prototype.forEach.call(dialog.querySelectorAll('[data-produktfinder-close]'), (b) => b.addEventListener('click', close));
    triggers.forEach((t) => t.addEventListener('click', (ev) => { ev.preventDefault(); open('header'); }));
    if (designMode()) {
      const section = dialog.closest('.shopify-section');
      document.addEventListener('shopify:section:select', (ev) => { if (section && ev.target === section) open('editor'); });
      document.addEventListener('shopify:section:deselect', (ev) => { if (section && ev.target === section) close(); });
    }
    const f = finder();
    if (f && f._resumedInModal) open('resume');
    win.ProduktfinderModal = { open, close, dialog };
    return win.ProduktfinderModal;
  }

  if (hasDOM) {
    try { defineElements(); } catch (e) { warn('Custom Elements konnten nicht registriert werden:', e); }
    try {
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => setupModal());
      else setupModal();
    } catch (e) { warn('Modal-Setup fehlgeschlagen:', e); }
  }
})();
